/**
 * SMSWiser Campaign Stats Tab (Drops)
 * Uses shared CampaignStatsDashboard with drops-specific data and actions.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useMemo } from 'react';

import { useAuth, useSmswiserCampaignDropsWithStats } from '@/hooks';

import { CampaignStatsDashboard } from './CampaignStatsDashboard';
import { TruncatedDropCell } from './cells';
import { splitDropTagsForDisplay } from './shared/utils/drop-tag-display';
import { FILTER_FIELDS, GROUP_BY_OPTIONS } from './shared/constants';
import type { GroupByKey } from './shared/constants';
import type { GenericStatsRow } from './shared/generic-stats.types';
import { toRangeISO } from './shared';
import { useSmswiserCampaignStatsTabData, useStatsCompareState } from './hooks';
import { SmswiserCampaignStatsTabActions } from './components/SmswiserCampaignStatsTabActions';
import { SmswiserCampaignStatsTabModals } from './components/SmswiserCampaignStatsTabModals';
import type { SmswiserCampaignStatsTabProps } from './components/SmswiserCampaignStatsTab.types';

export const SmswiserCampaignStatsTab: FC<SmswiserCampaignStatsTabProps> = ({
  countryKey,
  wiserIds,
  workDate,
  titlePrefix = '',
  onScaleAndSendComplete,
  perfTagsQuery,
}) => {
  const { user } = useAuth();
  const {
    state,
    scaleHandlers,
    rows,
    groups,
    totals,
    allGenericRows,
    query,
    selectedDropsForModals,
    routes,
    leadsLists,
    tlds,
    returnTo,
    countryLoading,
    staticTagFilterOptions,
    staticTagFilterColors,
    staticTagsLoading,
    tagFilterOptions,
    tagFilterColors,
    tagEmojis,
    scopedRuleNames,
    tagsLoading,
  } = useSmswiserCampaignStatsTabData({
    countryKey,
    wiserIds,
    workDate,
    onScaleAndSendComplete,
  });

  const compare = useStatsCompareState(state.fromDate, state.toDate, state.setDateRange);
  const compareRange = useMemo(
    () => toRangeISO(compare.compareFrom, compare.compareTo),
    [compare.compareFrom, compare.compareTo],
  );
  const compareQuery = useSmswiserCampaignDropsWithStats({
    countryKey,
    wiserIds,
    from: compareRange.from,
    to: compareRange.to,
    start: 0,
    length: state.length,
    currency: state.currency,
    tz: 'Europe/Paris',
    enabled: compare.compareMode,
  });
  const compareById = useMemo(() => {
    const map = new Map<string, Record<string, number> | null>();
    for (const r of compareQuery.data?.rows ?? []) {
      const uid = r?.drop?.uniqueId;
      if (uid) map.set(uid, r.voluum ?? null);
    }
    return map;
  }, [compareQuery.data?.rows]);

  const renderFirstCell = useCallback(
    (row: GenericStatsRow) => {
      const drop = row._originalDrop?.drop;
      if (!drop) return <span>{row.label ?? '—'}</span>;
      const { emojiTags, chipTags } = splitDropTagsForDisplay(
        row.performanceTags ?? [],
        {
          tagEmojis,
          scopedRuleNames,
          tagColors: tagFilterColors,
          // Scoped-rule tags only display in the ungrouped view; the row data
          // keeps them so the tag filter still works while grouped.
          hideScopedTags: state.groupBy !== 'none',
        },
      );
      return (
        <TruncatedDropCell
          drop={drop}
          dropSequence={row.sequence}
          dropPageHref={`/drops/${encodeURIComponent(drop.uniqueId)}`}
          returnTo={returnTo}
          countryKey={countryKey}
          performanceTags={chipTags}
          emojiTags={emojiTags}
          tagColors={tagFilterColors}
        />
      );
    },
    [returnTo, countryKey, tagEmojis, scopedRuleNames, tagFilterColors, state.groupBy],
  );

  const actionButtons = (
    <SmswiserCampaignStatsTabActions
      selectedCount={state.selectedRows.length}
      isLoading={countryLoading}
      isPending={scaleHandlers.createPrepared.isPending}
      onCopyAsDraft={() => state.setShowCopyAsDraftModal(true)}
      onScale={() => state.setShowScaleModal(true)}
    />
  );

  return (
    <>
      <CampaignStatsDashboard
        tab="drops"
        title="Drops"
        emptyMessage="No drops found for this range."
        loadingTitle="Crunching your performance data"
        loadingDesc="Matching Voluum metrics to your drops—impressions, conversions, ROI and more. Almost there."
        errorPrefix="Failed to load drop stats."
        firstColumnLabel="Drop List"
        rowUnitLabel="drop"
        filterFields={FILTER_FIELDS}
        groupByOptions={GROUP_BY_OPTIONS}
        rows={rows}
        totals={totals}
        groups={groups}
        totalRowCount={allGenericRows.length}
        allRowsLength={allGenericRows.length}
        currency={query.data?.currency ?? 'USD'}
        fromDate={state.fromDate}
        toDate={state.toDate}
        onFromChange={state.setFromDate}
        onToChange={state.setToDate}
        onDateRangeChange={state.setDateRange}
        length={state.length}
        onLengthChange={state.setLength}
        filtersState={state.filtersState}
        setFilters={state.setFilters}
        defaultFilter={state.defaultFilter}
        groupBy={state.groupBy}
        setGroupBy={(v) => state.setGroupBy(v as GroupByKey)}
        collapsedGroups={state.collapsedGroups}
        setCollapsedGroups={state.setCollapsedGroups}
        toggleGroup={state.toggleGroup}
        sort={state.sort}
        onSortClick={state.handleSortClick}
        selectedRows={state.selectedRows}
        setSelectedRows={state.setSelectedRows}
        renderFirstCell={renderFirstCell}
        isError={query.isError ?? false}
        error={query.error}
        isLoading={query.isLoading ?? false}
        isFetching={query.isFetching ?? false}
        dataUpdatedAt={query.dataUpdatedAt}
        refetch={() => query.refetch()}
        actionButtons={actionButtons}
        showLimitSelect
        staticTagFilterOptions={staticTagFilterOptions}
        csvExportUserId={user?.id}
        csvExportWorkDate={workDate}
        staticTagFilterColors={staticTagFilterColors}
        staticTagsLoading={staticTagsLoading}
        tagFilterOptions={tagFilterOptions}
        tagFilterColors={tagFilterColors}
        tagsLoading={tagsLoading}
        compareCapable={!groups}
        compareMode={compare.compareMode}
        onToggleCompare={compare.toggleCompareMode}
        compareFromDate={compare.compareFrom}
        compareToDate={compare.compareTo}
        onCompareFromChange={compare.setCompareFrom}
        onCompareToChange={compare.setCompareTo}
        onCompareRangeChange={compare.setCompareRange}
        compareById={compareById}
        compareTotals={compareQuery.data?.totals ?? {}}
        onComparePreset={compare.applyPreset}
        granularity={compare.granularity}
        onGranularityChange={compare.setGranularity}
        countryKey={countryKey}
        wiserIds={wiserIds}
      />

      <SmswiserCampaignStatsTabModals
        countryKey={countryKey}
        workDate={workDate}
        titlePrefix={titlePrefix}
        showCopyAsDraftModal={state.showCopyAsDraftModal}
        showScaleModal={state.showScaleModal}
        selectedDropsForModals={selectedDropsForModals}
        wiserIds={wiserIds}
        routes={routes}
        leadsLists={leadsLists}
        tlds={tlds.map((t) => ({ value: t.value, displayName: t.displayName }))}
        senderNames={scaleHandlers.creative.data?.senderNames ?? []}
        smsTexts={scaleHandlers.creative.data?.smsTexts ?? []}
        smsTextsWithIds={scaleHandlers.creative.data?.smsTextsWithIds}
        scaleHandlers={scaleHandlers}
        perfTagsQuery={perfTagsQuery}
        onCloseCopyAsDraft={() => state.setShowCopyAsDraftModal(false)}
        onCloseScale={() => state.setShowScaleModal(false)}
      />
    </>
  );
};

export default SmswiserCampaignStatsTab;
